<script setup lang="ts">
import { just, nothing } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { computed, nextTick, useTemplateRef, watch } from "vue";
import {
  initialLinkDialogModel,
  updateLinkDialogFn,
  type LinkDialogCmd,
} from "./LinkDialog.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";

const props = defineProps<{
  open: boolean;
  href: string;
  /** Editing an existing link: offer Remove. */
  canRemove: boolean;
}>();

const emit = defineEmits<{
  close: [];
  apply: [href: string];
  remove: [];
}>();

const input = useTemplateRef<InstanceType<typeof ZwInput>>("input");

async function runCmd(cmd: LinkDialogCmd): Promise<void> {
  switch (cmd.kind) {
    case "focusInput":
      await nextTick();
      input.value?.focus();
      input.value?.select();
      return;
    case "apply":
      emit("apply", cmd.href);
      return;
    default:
      assertNever(cmd);
  }
}

const { model, dispatch } = useTea(initialLinkDialogModel, updateLinkDialogFn, {
  runCmd: just(runCmd),
});

const draft = computed({
  get: (): string => model.value.draft,
  set: (value: string): void => {
    void dispatch({ type: "DraftChanged", draft: value });
  },
});

watch(
  () => props.open,
  (open) => {
    if (open === false) return;
    void dispatch({ type: "Seed", href: props.href });
  },
);

function apply(): void {
  void dispatch({ type: "Apply" });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    apply();
  }
}
</script>

<template>
  <ZwDialog
    :open="open"
    title="Link"
    :description="nothing"
    size="md"
    :z-index="105"
    @close="emit('close')"
  >
    <ZwInput
      :id="nothing"
      ref="input"
      v-model="draft"
      placeholder="https://"
      aria-label="Link address"
      :label="nothing"
      @keydown="onKeydown"
    />

    <template #footer>
      <ZwButton v-if="canRemove" @click="emit('remove')">Remove link</ZwButton>
      <ZwButton @click="emit('close')">Cancel</ZwButton>
      <ZwButton variant="primary" @click="apply">Save</ZwButton>
    </template>
  </ZwDialog>
</template>
